import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    auctionType,
    bidsType,
    membersType,
    memberType,
} from '../../types';
import './_styles_/BidOverview.css';
import ReadonlyField from '../fields/ReadonlyField';
import ReadonlyNumberField from '../fields/ReadonlyNumberField';
import {
    getBiddingType,
    OPEN,
    SLOTTED,
} from '../../utils/BiddingTypes';
import AutomaticallyCancelledBidsPopup from './AutomaticallyCancelledBidsPopup';
import AutomaticallyCancelledBidsField from './AutomaticallyCancelledBidsField';
import {
    CLOSED,
    FINISHED,
    getUIPhase,
    RUNNING,
} from '../../utils/AuctionPhase';
import SelectField from '../fields/SelectField';
import Collapsible from '../Collapsible';
import {BID_PRICE, getTradePriceCalculationType} from "../../utils/TradePriceCalculationTypes";

BidOverview.propTypes = {
    auction: auctionType.isRequired,
    bidsForMember: bidsType.isRequired,
    distinctMembers: membersType,
    onSelectedMemberIdChange: PropTypes.func.isRequired,
    selectedMember: memberType,
    automaticallyCancelledBidsForMember: bidsType.isRequired,
};

function BidOverview(props) { // NOSONAR javascript:S3776
    const {
        auction,
        bidsForMember,
        selectedMember = null,
        distinctMembers = [],
        onSelectedMemberIdChange,
        automaticallyCancelledBidsForMember,
    } = props;
    const { t } = useTranslation();
    const hasMoreMembers = distinctMembers.length > 1;
    const forAllMembers = !selectedMember && hasMoreMembers;
    const currencyCode = auction.zone.currency.code;
    const biddingType = getBiddingType(auction.biddingType);
    const tradeCalculationType = getTradePriceCalculationType(auction.tradePriceCalculationType);
    const uiPhase = getUIPhase(auction.phase);
    const activeBidsForMember = bidsForMember.filter((bid) => (!bid.obfuscated && !bid.cancelled));
    const [isAutomaticallyCancelledBidsTabVisible, setAutomaticallyCancelledBidsTabVisible] = useState(false);
    const shownMember = distinctMembers.length === 1 ? distinctMembers[0] : selectedMember;
    const shouldIncorporateExchangeRate = shownMember
        && auction.auctioneerMember.foreignCurrencyAllowed
        && auction.zone.currency.code === 'EUR'
        && shownMember.paymentCurrency.code !== 'EUR';
    let exchangeRatePostfix = null;
    if (shouldIncorporateExchangeRate) {
        exchangeRatePostfix = <>&nbsp;{shownMember.paymentCurrency.code}{t('auctions.bids-list.exchange-rate-to-euro')}</>;
    }
    const allocated = biddingType.is(OPEN) && uiPhase.is(FINISHED)
        ? activeBidsForMember.reduce((acc, bid) => acc + bid.quantityAllocated || 0, 0)
        : null;
    const valueOfExecutedBids = !uiPhase.is(FINISHED) ? null : (
        tradeCalculationType.is(BID_PRICE) ? auction.auctionResult.executedBidsPrice : (
            biddingType.is(OPEN)
                ? allocated * auction.auctionResult.finalPrice
                : null
        )
    );
    return (
        <Collapsible
            header={<h5>{t('auctions.bids-list.bid-overview')}</h5>}
            className="auction-bids--bid-overview bids-popup--inner-container"
        >
            <div className="auction-bids--bid-overview--container">
                <div className="auction-bids--bid-overview--fields--container">
                    <ReadonlyNumberField
                        label={t('auctions.bids-list.maximum-price')}
                        value={activeBidsForMember.reduce((previousPrice, bid) => (parseFloat(bid.price) > previousPrice ? parseFloat(bid.price) : previousPrice), 0)}
                        postfix={` ${currencyCode}`}
                        fractionDigits={2}
                        className="auction-bids--bid-overview--container--maximum-price"
                    />
                    <ReadonlyNumberField
                        label={t('auctions.bids-list.minimum-price')}
                        value={activeBidsForMember.length && activeBidsForMember.reduce((previousPrice, bid) => ((!previousPrice || parseFloat(bid.price) < previousPrice) ? parseFloat(bid.price) : previousPrice), null)}
                        postfix={` ${currencyCode}`}
                        fractionDigits={2}
                        className="auction-bids--bid-overview--container--minimum-price"
                    />
                    <ReadonlyNumberField
                        label={t('auctions.bids-list.average-price')}
                        value={activeBidsForMember.length && (activeBidsForMember.reduce((acc, bid) => acc + parseFloat(bid.price), 0) / activeBidsForMember.length)}
                        postfix={` ${currencyCode}`}
                        fractionDigits={2}
                        className="auction-bids--bid-overview--container--average-price"
                    />
                    <ReadonlyNumberField
                        label={t('auctions.bids-list.number-of-bids')}
                        value={bidsForMember.length}
                        className="auction-bids--bid-overview--container--number-of-bids"
                    />
                    {biddingType.is(OPEN) && (
                        <>
                            {(uiPhase.is(RUNNING) || uiPhase.is(CLOSED) || uiPhase.is(FINISHED)) && (
                                <ReadonlyNumberField
                                    label={t('auctions.bids-list.total-bids-quantities')}
                                    value={activeBidsForMember.reduce((acc, bid) => acc + bid.quantity || 0, 0)}
                                    className="auction-bids--bid-overview--container--total-bids-quantities"
                                />
                            )}
                            {uiPhase.is(FINISHED) && (
                                <>
                                    <ReadonlyNumberField
                                        label={t('auctions.bids-list.allocated')}
                                        value={allocated}
                                        className="auction-bids--bid-overview--container--allocated"
                                    />
                                    <ReadonlyNumberField
                                        label={t('auctions.bids-list.value-of-executed-bids')}
                                        value={valueOfExecutedBids}
                                        fractionDigits={2}
                                        postfix={<>&nbsp;{auction.zone.currency.code}</>}
                                        className="auction-bids--bid-overview--container--value-of-executed-bids"
                                    />
                                    {shouldIncorporateExchangeRate && (
                                        <ReadonlyNumberField
                                            label={t('auctions.bids-list.value-of-executed-bids-in-currency')}
                                            value={valueOfExecutedBids * shownMember.paymentCurrency.exchangeRate}
                                            fractionDigits={2}
                                            postfix={<>&nbsp;{shownMember.paymentCurrency.code}</>}
                                            className="auction-bids--bid-overview--container--value-of-executed-bids-in-currency"
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {biddingType.is(SLOTTED) && !forAllMembers && (
                        <ReadonlyField
                            label={t('auctions.bids-list.number-of-free-slots')}
                            value={auction.quantity - bidsForMember.length}
                            className="auction-bids--bid-overview--container--number-of-free-slots"
                        />
                    )}
                    {shouldIncorporateExchangeRate && (
                        <ReadonlyNumberField
                            label={t('auctions.bids-list.exchange-rate')}
                            value={shownMember.paymentCurrency.exchangeRate}
                            className="auction-bids--bid-overview--container--exchange-rate"
                            fractionDigits={4}
                            postfix={exchangeRatePostfix}
                        />
                    )}
                    {biddingType.is(OPEN) && (
                        <AutomaticallyCancelledBidsField
                            automaticallyCancelledBidsForMember={automaticallyCancelledBidsForMember}
                            onShowTable={() => setAutomaticallyCancelledBidsTabVisible(true)}
                        />
                    )}
                </div>
                {hasMoreMembers && (
                    <div className="auction-bids--bid-overview--configuration">
                        <SelectField
                            value={selectedMember ? selectedMember.memberId : ''}
                            label={t('auctions.bids-list.show')}
                            className="auction-bids--bid-overview--configuration--upper-row--members"
                            onChange={onSelectedMemberIdChange}
                            emptyOption={<option value="">{t('auctions.bids-list.all-members')}</option>}
                            options={distinctMembers.sort((member1, member2) => member1.memberId.localeCompare(member2.memberId)).map((member) => (
                                <option value={member.memberId} key={member.memberId}>
                                    {member.memberId}{member.balancingGroup ? ` (${member.balancingGroup})` : null}
                                </option>
                            ))}
                        />
                    </div>
                )}
                {isAutomaticallyCancelledBidsTabVisible && (
                    <AutomaticallyCancelledBidsPopup
                        auction={auction}
                        automaticallyCancelledBidsForMember={automaticallyCancelledBidsForMember}
                        onCancel={() => setAutomaticallyCancelledBidsTabVisible(false)}
                    />
                )}
            </div>
        </Collapsible>
    );
}

export default BidOverview;
